import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil } from "lucide-react";
import { bookingRequest } from "./bookingApi";

const EMPTY_FORM = {
  name: "",
  description: "",
  duration_minutes: 30,
  price_dollars: "",
  buffer_before_minutes: 0,
  buffer_after_minutes: 0,
  active: true,
  // Empty means "any active provider may perform this service" - the
  // backend's own default and documented meaning (ServiceCreate.provider_ids).
  provider_ids: [],
};

const centsToDollars = (cents) => (cents / 100).toFixed(2);

const ServicesTab = ({ basePath, token }) => {
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [providersError, setProvidersError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    // allSettled: a failing /providers call (only needed for the
    // "Qualified providers" checkboxes) must not hide the services list.
    const [servicesRes, providersRes] = await Promise.allSettled([
      bookingRequest(basePath, token, "GET", "/services", {
        params: { active_only: showInactive ? "false" : "true" },
      }),
      bookingRequest(basePath, token, "GET", "/providers", { params: { active_only: "true" } }),
    ]);
    if (servicesRes.status === "fulfilled") {
      setServices(servicesRes.value?.items || []);
    } else {
      toast.error(servicesRes.reason?.message || "Failed to load services");
    }
    if (providersRes.status === "fulfilled") {
      setProviders(providersRes.value?.items || []);
      setProvidersError(false);
    } else {
      setProvidersError(true);
      toast.error(providersRes.reason?.message || "Failed to load providers");
    }
    setLoading(false);
  }, [basePath, token, showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleProvider = (providerId, checked) => {
    setForm((prev) => ({
      ...prev,
      provider_ids: checked
        ? [...prev.provider_ids, providerId]
        : prev.provider_ids.filter((id) => id !== providerId),
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (service) => {
    setEditingId(service.id);
    setForm({
      name: service.name || "",
      description: service.description || "",
      duration_minutes: service.duration_minutes || 30,
      price_dollars: centsToDollars(service.price_cents || 0),
      buffer_before_minutes: service.buffer_before_minutes || 0,
      buffer_after_minutes: service.buffer_after_minutes || 0,
      active: service.active !== false,
      provider_ids: service.provider_ids || [],
    });
    setFormError("");
    setDialogOpen(true);
  };

  // Duration/buffers must land on the backend's 5-minute slot grid -
  // caught here so a rejected value is rare, not the normal path.
  const onFiveMinuteField = (field) => (e) => {
    const value = Number(e.target.value);
    setForm({ ...form, [field]: Number.isFinite(value) ? value : 0 });
  };

  const submit = async (event) => {
    event.preventDefault();
    const dollars = Number(form.price_dollars);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setFormError("Enter a valid price");
      return;
    }
    // Mirrors backend.booking's ServiceCreate/ServiceUpdate bounds
    // (duration <= 8h, buffers <= 4h) - checked here so a rejected value
    // is rare, not the normal path, and so the error is this same
    // friendly message rather than a raw 422 body.
    const maxMinutes = { duration_minutes: 480, buffer_before_minutes: 240, buffer_after_minutes: 240 };
    for (const field of ["duration_minutes", "buffer_before_minutes", "buffer_after_minutes"]) {
      if (form[field] % 5 !== 0) {
        setFormError(`${field.replace(/_/g, " ")} must be a multiple of 5 minutes`);
        return;
      }
      if (form[field] > maxMinutes[field]) {
        setFormError(`${field.replace(/_/g, " ")} must be ${maxMinutes[field]} minutes or less`);
        return;
      }
    }
    setFormError("");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        duration_minutes: form.duration_minutes,
        price_cents: Math.round(dollars * 100),
        buffer_before_minutes: form.buffer_before_minutes,
        buffer_after_minutes: form.buffer_after_minutes,
        active: form.active,
        // Always sent, even when empty: an edit that unchecks every
        // provider must actually clear a prior restriction, not leave it
        // untouched (the backend's PUT only $sets fields present in the
        // request body).
        provider_ids: form.provider_ids,
      };
      if (editingId) {
        await bookingRequest(basePath, token, "PUT", `/services/${editingId}`, { body: payload });
        toast.success("Service updated");
      } else {
        await bookingRequest(basePath, token, "POST", "/services", { body: payload });
        toast.success("Service added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setFormError(err.message || "Could not save service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-panel border-white/10" data-testid="services-tab">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm text-white/70">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} />
            Show inactive services
          </label>
          <Button className="primary-btn" onClick={openCreate} data-testid="add-service-btn">
            <Plus size={16} />
            Add Service
          </Button>
        </div>

        {loading ? (
          <p className="text-white/60 text-sm">Loading services...</p>
        ) : services.length === 0 ? (
          <p className="text-white/60 text-sm py-8 text-center">No services on the menu yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Service</TableHead>
                <TableHead className="text-white/60">Duration</TableHead>
                <TableHead className="text-white/60">Price</TableHead>
                <TableHead className="text-white/60">Providers</TableHead>
                <TableHead className="text-white/60">Status</TableHead>
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                  <TableCell className="text-white/90">{service.name}</TableCell>
                  <TableCell className="text-white/70">{service.duration_minutes} min</TableCell>
                  <TableCell className="text-white/70">${centsToDollars(service.price_cents)}</TableCell>
                  <TableCell className="text-white/70">
                    {service.provider_ids && service.provider_ids.length > 0
                      ? `${service.provider_ids.length} qualified`
                      : "Any"}
                  </TableCell>
                  <TableCell className="text-white/70">{service.active ? "Active" : "Inactive"}</TableCell>
                  <TableCell className="text-right">
                    <Button className="ghost-btn text-xs" onClick={() => openEdit(service)} data-testid={`edit-service-${service.id}`}>
                      <Pencil size={14} /> Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingId ? "Edit Service" : "Add Service"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-white/70">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/70">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Duration (minutes)</Label>
                <Input type="number" step={5} min={5} max={480} required value={form.duration_minutes} onChange={onFiveMinuteField("duration_minutes")} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Price ($)</Label>
                <Input type="number" step="0.01" min={0} required value={form.price_dollars} onChange={(e) => setForm({ ...form, price_dollars: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Buffer before (minutes)</Label>
                <Input type="number" step={5} min={0} max={240} value={form.buffer_before_minutes} onChange={onFiveMinuteField("buffer_before_minutes")} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Buffer after (minutes)</Label>
                <Input type="number" step={5} min={0} max={240} value={form.buffer_after_minutes} onChange={onFiveMinuteField("buffer_after_minutes")} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            <div>
              <Label className="text-white/70 mb-1 block">Qualified providers</Label>
              <p className="text-white/40 text-xs mb-2">
                Leave all unchecked to let any active provider perform this service.
              </p>
              {providersError ? (
                <p className="text-white/50 text-sm">Couldn't load providers. This service's existing provider selections are kept.</p>
              ) : providers.length === 0 ? (
                <p className="text-white/50 text-sm">No active providers set up yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {providers.map((provider) => (
                    <label key={provider.id} className="flex items-center gap-2 text-sm text-white/70">
                      <Checkbox
                        checked={form.provider_ids.includes(provider.id)}
                        onCheckedChange={(v) => toggleProvider(provider.id, !!v)}
                        data-testid={`service-provider-${provider.id}`}
                      />
                      {provider.full_name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-white/70">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              Active (bookable)
            </label>
            {formError && <div className="form-error">{formError}</div>}
            <DialogFooter>
              <Button type="button" className="ghost-btn" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="primary-btn" disabled={saving} data-testid="save-service-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ServicesTab;
